import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { MarginOrderDirection } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { useMarginTradePanelStoreUtils } from 'src/lib/margin/hooks/trade';

type Props = {
  tradeSide: MarginOrderDirection;
  valueOnly?: boolean;
};

const MarginTradePanelMarginFee: React.FC<Props> = ({ tradeSide, valueOnly = false }) => {
  const { lots, price } = useMarginTradePanelStoreUtils({ tradeSide });
  const { getCurrencyRate } = useMarginPrices();
  const { selectedMarket } = useMarginInstruments();
  const { currency, commission } = useMarginAccount();
  const { t } = useTranslation('margin');

  const estimatedCommission = useMemo(() => {
    if (!price) return Big(0);

    return Big(lots)
      .times(selectedMarket?.contractSize || 1)
      .times(price)
      .times(getCurrencyRate())
      .times(commission);
  }, [commission, getCurrencyRate, lots, price, selectedMarket?.contractSize]);

  return (
    <FlexLayout alignItems='center' justifyContent='space-between'>
      {!valueOnly && (
        <Body color='secondary' underline='dashed' className='cursor-pointer'>
          {t('tradeReview.estimatedCommission')}
        </Body>
      )}
      <FlexLayout spacing={2}>
        <Numeric color={estimatedCommission.eq(0) ? 'disabled' : 'primary'} weight='bold'>
          ≈ {formatCurrency(estimatedCommission.round(2, 0), currency, { hideCode: false, appendCode: true })}
        </Numeric>
      </FlexLayout>
    </FlexLayout>
  );
};

export { MarginTradePanelMarginFee };
